/**
 * Tiny seeded RNG for daily modes. Mulberry32 — 32-bit PRNG, ~5 lines,
 * uniform distribution good enough for game prompts (NOT cryptographic).
 *
 * Used to make daily prompts deterministic per local date so every player
 * picking up the game on the same wall-clock day gets the same N picks
 * in the same order. The seed comes from `dateToSeed(date)`; all picker
 * randomness flows through the returned function.
 *
 * Reference: https://github.com/bryc/code/blob/master/jshash/PRNGs.md#mulberry32
 */
export function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Derive a 32-bit seed from a `YYYY-MM-DD` date string via FNV-1a hash.
 * Same string → same seed → same pick order. Cheap, deterministic,
 * collision-rare-enough for our use (one seed per day, not per session).
 */
export function dateToSeed(dateString: string): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < dateString.length; i++) {
    hash ^= dateString.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return hash >>> 0;
}
